import { Injectable, inject } from '@angular/core';
import {
  CompteEntreprise,
  Contact,
  Invitation,
  CompanyMscompany,
  CompanyDetails,
  Users,
} from '../models/compteEntreprise.model';
import { HttpClient } from '@angular/common/http';
import { DetailsEntreprise } from '../models/detailsEntreprise.model';
import { environment } from '@env/environment';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { GS1NotificationService } from '@gs1france/gs1-components-library';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class CompteEntrepriseService {
  __URL_API__ = `${environment.URL_API}`;

  private _http = inject(HttpClient);
  private notificationService = inject(GS1NotificationService);
  private translate = inject(TranslateService);

  getCompteEntreprise(): Observable<CompteEntreprise> {
    return this._http
      .get<CompteEntreprise>(this.__URL_API__ + 'api/Entreprise/GetCompteByNum')
      .pipe(catchError((error) => this.handleError(error)));
  }

  getMSCompanyEntreprise(accountNumber: string): Observable<CompanyMscompany> {
    return this._http
      .get<CompanyMscompany>(this.__URL_API__ + 'api/Entreprise/GetMsCompanyEntreprise?accountNumber=' + accountNumber)
      .pipe(catchError((error) => this.handleError(error)));
  }

  getDtailsEntreprise(siren: string): Observable<DetailsEntreprise[]> {
    return this._http
      .get<DetailsEntreprise[]>(this.__URL_API__ + 'api/Entreprise/GetDetailEntreprise?siren=' + siren)
      .pipe(catchError((error) => this.handleError(error)));
  }

  getCompanyDetails(): Observable<CompanyDetails> {
    return this._http
      .get<CompanyDetails>(this.__URL_API__ + 'api/companies')
      .pipe(catchError((err) => this.handleError(err)));
  }

  updateCompteEntreprise(compteEntrepriseForm: CompteEntreprise): Observable<string> {
    const formData: FormData = new FormData();
    formData.append('nom', compteEntrepriseForm.nom == undefined ? '' : compteEntrepriseForm.nom.toString());
    formData.append(
      'adresse',
      compteEntrepriseForm.adresse == undefined ? '' : compteEntrepriseForm.adresse.toString()
    );
    formData.append(
      'adresseComp',
      compteEntrepriseForm.adresseComp == undefined ? '' : compteEntrepriseForm.adresseComp.toString()
    );
    formData.append(
      'codePostale',
      compteEntrepriseForm.codePostale == undefined ? '' : compteEntrepriseForm.codePostale.toString()
    );
    formData.append('ville', compteEntrepriseForm.ville == undefined ? '' : compteEntrepriseForm.ville.toString());
    formData.append('pays', compteEntrepriseForm.pays == undefined ? '' : compteEntrepriseForm.pays.toString());
    formData.append(
      'telephone',
      compteEntrepriseForm.telephone == undefined ? '' : compteEntrepriseForm.telephone.toString()
    );
    formData.append('siren', compteEntrepriseForm.siren == undefined ? '' : compteEntrepriseForm.siren.toString());

    return this._http
      .put<string>(this.__URL_API__ + 'api/Entreprise/Update', formData)
      .pipe(catchError((error) => this.handleError(error)));
  }

  updateMscompany(company: CompanyMscompany): Observable<void> {
    return this._http
      .patch<void>(this.__URL_API__ + 'api/Entreprise/UpdateMsCompany', company)
      .pipe(catchError((error) => this.handleError(error)));
  }

  updateCompanyDetails(company: CompanyDetails): Observable<CompanyDetails> {
    return this._http
      .patch<CompanyDetails>(this.__URL_API__ + 'api/companies', company, {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        tap(() =>
          this.notificationService.addNotification(this.translate.instant('Company.Activity.updateSuccess'), 'success')
        ),
        catchError((err) => this.handleError(err, 'Company.Activity.updateError'))
      );
  }

  retrieveContacts(): Observable<Contact[]> {
    return this._http
      .get<Contact[]>(this.__URL_API__ + 'api/Entreprise/ListeContacts')
      .pipe(catchError((error) => this.handleError(error)));
  }

  retrieveInvitations(): Observable<Invitation[]> {
    return this._http
      .get<Invitation[]>(this.__URL_API__ + 'api/Entreprise/ListeInvitations')
      .pipe(catchError((error) => this.handleError(error)));
  }

  getUsers(): Observable<Users> {
    return this._http.get<Users>(this.__URL_API__ + 'api/User').pipe(
      catchError((error) => {
        this.handleError(error, 'Users.load-error');
        return throwError(() => error);
      })
    );
  }

  getSpecialUsers(): Observable<{ count: number; invitations: Invitation[] }> {
    return this._http
      .get<{
        count: number;
        invitations: Invitation[];
      }>(this.__URL_API__ + 'api/User?isMain=true&isBilling=true')
      .pipe(catchError((error) => this.handleError(error, 'Users.load-error')));
  }

  deleteUser(id: string, idContact: string, name: string): Observable<any> {
    return this._http
      .patch<{
        count: number;
        invitations: Invitation[];
      }>(this.__URL_API__ + 'api/User/delete?idInvit =' + id + '&idContact=' + idContact, null)
      .pipe(
        tap(() =>
          this.notificationService.addNotification(
            this.translate.instant('Users.Delete.success', { name }),
            'success',
            15000
          )
        ),
        tap(() => idContact),
        catchError((error) => this.handleError(error, 'Users.load-error'))
      );
  }

  deleteInvit(id: string, name: string): Observable<any> {
    const url = `${this.__URL_API__}api/User/cancelInvit/${id}`;
    return this._http.patch(url, null).pipe(
      tap(() =>
        this.notificationService.addNotification(
          this.translate.instant('Users.Delete.success', { name }),
          'success',
          15000
        )
      ),
      tap(() => id),
      catchError((error) => this.handleError(error, 'Users.load-error'))
    );
  }

  private handleError(error: any, message?: string): Observable<never> {
    if (message) {
      this.notificationService.addNotification(this.translate.instant(message), 'error');
    }
    return throwError(() => error.message || error);
  }
}
